// Settings — sound + music sliders, ONE mute for all audio, music vibe
// picker, theme picker (+ the Advanced reveal-everything toggle in-world).
// Theme persists in localStorage and applies via html[data-theme]; colors all
// live in css/theme.css. Audio settings persist in the shared [audio] blob
// (audio/ctx.ts, localStorage).

import { el, modal } from '../kit';
import { sendEvent } from '../net';
import * as vsj from '../vsj';
import * as sfx from '../audio/sfx';
import * as music from '../audio/music';

const THEMES: Record<string, string> = { ashen: 'Ashen', slate: 'Slate' };
const KEY = 'stoneheim.theme';

export function applySavedTheme(): void {
  const t = localStorage.getItem(KEY);
  if (t && THEMES[t]) document.documentElement.dataset.theme = t;
}

// Reduced motion: html[data-motion="reduced"] — CSS reads the attribute,
// JS juice checks ui.reducedMotion(). Persists like the theme.
const MOTION_KEY = 'stoneheim.motion';

export function applySavedMotion(): void {
  if (localStorage.getItem(MOTION_KEY) === 'reduced')
    document.documentElement.dataset.motion = 'reduced';
}

function motionReduced(): boolean {
  return document.documentElement.dataset.motion === 'reduced';
}

function setReducedMotion(on: boolean): void {
  if (on) {
    document.documentElement.dataset.motion = 'reduced';
    localStorage.setItem(MOTION_KEY, 'reduced');
  } else {
    delete document.documentElement.dataset.motion;
    localStorage.removeItem(MOTION_KEY);
  }
}

export function openSettings(showAdvanced: boolean): void {
  const m = modal({ dismissable: true });
  m.body.style.minWidth = '320px';
  m.body.append(el('div', { class: 'k-title', text: 'Settings' }));

  // ---- audio ----
  const volRow = (
    label: string,
    get: () => number,
    setLive: (v: number) => void,
    setFinal: (v: number) => void,
  ) => {
    const pct = el('span', { class: 'k-dim k-small', style: 'width:38px;text-align:right' });
    const slider = el('input', {
      type: 'range', min: '0', max: '100', value: String(Math.round(get() * 100)),
      style: 'flex:1',
    });
    const refresh = () => (pct.textContent = `${Math.round(get() * 100)}%`);
    // live preview while dragging; persist on release
    slider.addEventListener('input', () => {
      setLive(Number(slider.value) / 100);
      refresh();
    });
    slider.addEventListener('change', () => setFinal(Number(slider.value) / 100));
    refresh();
    return el(
      'div',
      { style: 'display:flex;gap:6px;align-items:center' },
      el('span', { class: 'k-dim', style: 'width:52px', text: label }),
      slider,
      pct,
    );
  };
  m.body.append(
    volRow('Sound', () => sfx.volume, (v) => sfx.setVolume(v, false), (v) => {
      sfx.setVolume(v, true);
      sfx.playEvent('volume_preview'); // audible "this loud"
    }),
    // music is its own bed — hearing the change live IS the preview
    volRow('Music', () => music.volume, (v) => music.setVolume(v, false), (v) => music.setVolume(v, true)),
  );
  const muteBtn = el('button', { class: 'k-btn', text: sfx.muted ? 'Unmute' : 'Mute' });
  muteBtn.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted);
    music.setMuted(sfx.muted); // ONE mute rules all audio
    if (!sfx.muted) sfx.playEvent('volume_preview');
    muteBtn.textContent = sfx.muted ? 'Unmute' : 'Mute';
  });
  m.body.append(muteBtn);

  const vibeRow = el('div', { style: 'display:flex;gap:6px;align-items:center;flex-wrap:wrap' });
  vibeRow.append(el('span', { class: 'k-dim', style: 'flex-basis:100%', text: 'Music vibe' }));
  for (const [id, v] of Object.entries(music.VIBES)) {
    const b = el('button', {
      class: 'k-btn compact' + (id === music.vibeName ? ' active' : ''),
      text: v.label,
    });
    b.addEventListener('click', () => {
      sfx.playEvent('ui_tick');
      music.setVibe(id); // re-renders in the background; swaps when ready
      vibeRow.querySelectorAll('.k-btn').forEach((n) => n.classList.remove('active'));
      b.classList.add('active');
    });
    vibeRow.append(b);
  }
  m.body.append(vibeRow);

  const themeRow = el('div', { style: 'display:flex;gap:6px;align-items:center' });
  themeRow.append(el('span', { class: 'k-dim', style: 'flex:1', text: 'Theme' }));
  const current = document.documentElement.dataset.theme ?? 'ashen';
  for (const [id, label] of Object.entries(THEMES)) {
    const b = el('button', { class: 'k-btn' + (id === current ? ' active' : ''), text: label });
    b.addEventListener('click', () => {
      sfx.playEvent('ui_tick');
      document.documentElement.dataset.theme = id;
      localStorage.setItem(KEY, id);
      themeRow.querySelectorAll('.k-btn').forEach((n) => n.classList.remove('active'));
      b.classList.add('active');
    });
    themeRow.append(b);
  }
  m.body.append(themeRow);

  // ---- motion: keep every state change, drop the movement ----
  const motionBtn = el('button', {
    class: 'k-btn' + (motionReduced() ? ' active' : ''),
    text: 'Reduce motion',
  });
  motionBtn.addEventListener('click', () => {
    sfx.playEvent('ui_tick');
    setReducedMotion(!motionReduced());
    motionBtn.classList.toggle('active', motionReduced());
  });
  m.body.append(
    el('div', { style: 'display:flex;gap:6px;align-items:center' },
      el('span', { class: 'k-dim', style: 'flex:1', text: 'Motion' }),
      motionBtn),
  );

  if (showAdvanced) {
    const eyeOn = !!vsj.at('reveal._eye');
    const eye = el('button', {
      class: 'k-btn' + (eyeOn ? ' active' : ''),
      text: eyeOn ? 'Reveal everything: on' : 'Reveal everything: off',
    });
    eye.addEventListener('click', () => {
      sendEvent('toggle_eye');
      m.close();
    });
    m.body.append(
      el('div', { class: 'k-faint k-small', text: 'Advanced' }),
      eye,
    );
  }

  m.body.append(el('button', {
    class: 'k-btn', text: 'Close',
    onclick: () => {
      sfx.playEvent('ui_tick');
      m.close();
    },
  }));
}
